import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, CalendarCheck, CheckCircle2, ShieldCheck, Quote } from "lucide-react";
import Footer from "@/components/Footer";
import BackToTop from "@/components/BackToTop";
import PublicHeader from "@/components/PublicHeader";
import { useMeta } from "@/lib/useMeta";
import { INDUSTRIES, INDUSTRY_LIST } from "./industryData";

const GOLD = "#C9A84C";

const Gold = ({ children, className = "" }) => (
  <span className={className} style={{ color: GOLD }}>{children}</span>
);

export default function IndustryPage() {
  const { slug } = useParams();
  const data = INDUSTRIES[slug];
  useMeta({ title: data?.meta.title, description: data?.meta.description, path: `/industry/${slug}` });

  if (!data) return <Navigate to="/features" replace />;
  const Icon = data.icon;
  const others = INDUSTRY_LIST.filter((i) => i.slug !== slug);

  return (
    <div className="min-h-screen bg-[#0A1628] text-white" data-testid={`industry-page-${slug}`}>
      <PublicHeader />

      <section className="relative min-h-[440px] md:min-h-[520px] flex items-end" data-testid="industry-hero">
        <img src={data.hero} alt={`${data.name} project site`} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628] via-[#0A1628]/70 to-[#0A1628]/35" />
        <div className="relative max-w-[1200px] mx-auto px-5 sm:px-8 pb-14 pt-24 w-full">
          <div className="flex items-center gap-2.5">
            <Icon className="w-5 h-5" style={{ color: GOLD }} />
            <span className="text-[11px] uppercase tracking-[0.18em]" style={{ color: GOLD }}>Industry</span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.08] mt-4" data-testid="industry-title">
            {data.name}
          </h1>
          <p className="text-base sm:text-lg text-white/85 mt-5 max-w-[760px] leading-relaxed" data-testid="industry-tagline">
            {data.tagline}
          </p>
          {data.badge ? (
            <div className="inline-flex items-center gap-2 mt-6 border px-3 py-1.5 rounded-lg text-[12px]"
                 style={{ borderColor: "rgba(201,168,76,0.45)", color: GOLD, background: "rgba(201,168,76,0.08)" }}
                 data-testid="industry-badge">
              <ShieldCheck className="w-4 h-4" /> {data.badge}
            </div>
          ) : null}
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/signup" className="inline-flex items-center h-11 px-6 rounded-lg text-[15px] font-semibold text-white transition-colors duration-200"
                  style={{ backgroundColor: GOLD }} data-testid="industry-hero-trial-btn">
              Start Free Trial <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
              <Link to="/demo" className="inline-flex items-center h-11 px-6 rounded-lg border border-white/35 text-[15px] font-medium text-white hover:bg-white/10 transition-colors duration-200"
                  data-testid="industry-hero-demo-btn">
              <CalendarCheck className="w-4 h-4 mr-2" /> Book a live demo
            </Link>
            <Link to={`/demo/${slug}`} className="inline-flex items-center h-11 px-6 rounded-lg border text-[15px] font-semibold transition-colors duration-200 hover:bg-[#C9A84C]/10"
                  style={{ borderColor: GOLD, color: GOLD }} data-testid="industry-see-demo-btn">
              See Live Demo <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
        <p className="text-base text-white/75 leading-relaxed max-w-[840px] py-12" data-testid="industry-intro">{data.intro}</p>

        <section className="pb-14" data-testid="industry-challenges">
          <h2 className="text-base md:text-lg font-semibold uppercase tracking-[0.14em]"><Gold>The challenges</Gold></h2>
          <div className="grid sm:grid-cols-2 gap-3 mt-6">
            {data.challenges.map(([title, text], i) => (
              <div key={title} className="border border-white/10 bg-[#112240] rounded-lg p-5" data-testid={`challenge-${i}`}>
                <span className="num text-[11px]" style={{ color: GOLD }}>0{i + 1}</span>
                <h3 className="font-semibold text-[14.5px] mt-2">{title}</h3>
                <p className="text-[13px] text-white/60 mt-2 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="pb-14" data-testid="industry-solutions">
          <h2 className="text-base md:text-lg font-semibold uppercase tracking-[0.14em]"><Gold>How Plan-Sarthi solves it</Gold></h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-6">
            {data.features.map(([FIcon, title, text], i) => (
              <div key={title} className="border border-white/10 bg-[#112240] rounded-lg p-5 hover:border-[#C9A84C]/40 transition-colors duration-200" data-testid={`solution-${i}`}>
                <FIcon className="w-5 h-5" style={{ color: GOLD }} />
                <h3 className="font-semibold text-[14.5px] mt-3">{title}</h3>
                <p className="text-[13px] text-white/60 mt-2 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="pb-14" data-testid="industry-metrics">
          <div className="grid sm:grid-cols-3 gap-3">
            {data.metrics.map(([value, label], i) => (
              <div key={label} className="border rounded-lg p-6" style={{ borderColor: "rgba(201,168,76,0.3)", background: "rgba(201,168,76,0.06)" }} data-testid={`metric-${i}`}>
                <div className="font-display text-2xl sm:text-3xl font-bold" style={{ color: GOLD }}>{value}</div>
                <p className="text-[13px] text-white/70 mt-2 leading-relaxed">{label}</p>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-white/40 mt-3">
            Indicative of what the platform makes measurable on a typical package; actual results depend on your project and data discipline.
          </p>
        </section>

        <section className="pb-14" data-testid="industry-lifecycle">
          <h2 className="text-base md:text-lg font-semibold uppercase tracking-[0.14em]"><Gold>Where Plan-Sarthi sits in the lifecycle</Gold></h2>
          <div className="mt-7 relative">
            <div className="hidden md:block absolute top-[11px] left-0 right-0 h-px" style={{ background: "rgba(201,168,76,0.3)" }} />
            <div className="grid md:grid-cols-5 gap-6 md:gap-3 relative">
              {data.lifecycle.map((stage, i) => (
                <div key={stage} className="flex md:block items-center gap-3" data-testid={`lifecycle-${i}`}>
                  <span className="w-[22px] h-[22px] rounded-full border-2 flex items-center justify-center shrink-0 bg-[#0A1628]"
                        style={{ borderColor: GOLD }}>
                    <CheckCircle2 className="w-3 h-3" style={{ color: GOLD }} />
                  </span>
                  <div className="md:mt-3.5">
                    <div className="text-[10.5px] num" style={{ color: GOLD }}>STAGE {i + 1}</div>
                    <div className="text-[13px] font-medium mt-0.5">{stage}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="pb-14" data-testid="industry-testimonial">
          <div className="border border-white/10 bg-[#112240] rounded-lg p-6 sm:p-8 max-w-[820px]">
            <Quote className="w-6 h-6" style={{ color: GOLD }} />
            <p className="text-base sm:text-lg leading-relaxed mt-4 text-white/90">"{data.testimonial.quote}"</p>
            <div className="mt-5 text-[12.5px] text-white/55">
              <span className="text-white font-medium">{data.testimonial.name}</span> · {data.testimonial.designation}
              <br />{data.testimonial.company}
            </div>
          </div>
        </section>

        <section className="pb-14" data-testid="industry-cta">
          <div className="border rounded-lg p-7 sm:p-10 text-center" style={{ borderColor: "rgba(201,168,76,0.35)", background: "rgba(201,168,76,0.07)" }}>
            <h2 className="font-display text-2xl sm:text-3xl font-bold">
              Managing a <Gold>{data.name}</Gold> project? Start your free 14-day trial.
            </h2>
            <p className="text-[13.5px] text-white/65 mt-3 max-w-[600px] mx-auto leading-relaxed">
              No card, no obligation. Bring one live work front and see the S-curve, critical path and earned value
              build themselves from your daily reports.
            </p>
            <div className="flex flex-wrap gap-3 justify-center mt-7">
              <Link to="/signup" className="inline-flex items-center h-11 px-6 rounded-lg text-[15px] font-semibold text-white"
                    style={{ backgroundColor: GOLD }} data-testid="industry-cta-trial-btn">
                Start Free Trial <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
              <Link to="/features" className="inline-flex items-center h-11 px-6 rounded-lg border border-white/35 text-[15px] text-white hover:bg-white/10 transition-colors duration-200" data-testid="industry-features-link">
                Explore the platform
              </Link>
              <Link to="/pricing" className="inline-flex items-center h-11 px-6 rounded-lg border border-white/35 text-[15px] text-white hover:bg-white/10 transition-colors duration-200" data-testid="industry-pricing-link">
                See pricing
              </Link>
            </div>
          </div>
        </section>

        <section className="pb-16" data-testid="industry-others">
          <h2 className="text-base md:text-lg font-semibold uppercase tracking-[0.14em]"><Gold>Explore other industries</Gold></h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-6">
            {others.map((o) => (
              <Link
                key={o.slug}
                to={`/industry/${o.slug}`}
                className="flex items-start gap-3 border border-white/10 bg-[#112240] rounded-lg p-4 hover:border-[#C9A84C]/50 transition-colors duration-200"
                data-testid={`other-industry-${o.slug}`}
              >
                <o.icon className="w-4 h-4 mt-0.5 shrink-0" style={{ color: GOLD }} />
                <div>
                  <div className="font-medium text-[13.5px]">{o.name}</div>
                  <div className="text-[11.5px] text-white/50 mt-1 line-clamp-2">{o.tagline}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </div>

      <Footer />
      <BackToTop />
    </div>
  );
}
